import { ReactNode, Suspense, useEffect, useState } from 'react';

import {
  IconButton as ButtonIcon,
  Box as Flex,
  Typography as Text,
  Tooltip,
} from '@material-ui/core';

import { useApolloClient } from '@apollo/client';
import {
  RiDownloadLine,
  RiExternalLinkLine,
  RiFileLine,
} from '@remixicon/react';

import {
  Copy,
  DateUtils,
  DialogCompositeMui as DialogComposite,
  ErrorBoundary,
  Spinner,
  downloadTextFile,
  sanitizeFilename,
  useDialog,
  useDocumentNavigation,
} from '@spotify/confidence-core-react';
import {
  CollaborativeDocumentProvider,
  CommentDecorationsExtension,
  CommentMark,
  CopyAsMarkdownButton,
  TipTapEditor,
  useCollaborativeDocument,
  useCollaborativeDocumentContext,
} from '@spotify/confidence-editor-react';
import {
  AttachmentFragment,
  CollaborativeDocumentDocument,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import {
  ArtifactCardBase,
  ArtifactIcon,
  ArtifactListItem,
  ArtifactMenu,
  createArtifactExtension,
  createModifierClickHandler,
  getArtifactDisplayName,
  isExternalLink,
} from '@spotify/plugin-confidence-artifacts-react';
import {
  MarkdownContent,
  useRunningChatNames,
} from '@spotify/plugin-confidence-chat-react';
import { UNTITLED_DOCUMENT_TITLE } from '@spotify/plugin-confidence-collaboration-react';
import {
  IdentityChipMui as IdentityChip,
  identityKind,
} from '@spotify/plugin-confidence-iam-react';
import { getResourceName } from '@spotify/plugin-confidence-projects-react';
import {
  MetricCard as ReportMetricCard,
  MetricTable as ReportMetricTable,
  VariantCard as ReportVariantCard,
} from '@spotify/plugin-confidence-workflows-module-report';

import { projectsPlugin } from '../plugin';

import styles from './DocumentArtifactExtension.module.css';

const DOCUMENT_POLL_INTERVAL_MS = 10_000;
const DOCUMENT_UPDATING_POLL_INTERVAL_MS = 2_000;

const reflowSingleLineTables = (markdown: string): string =>
  markdown.replace(/\|(?:[^|\n]*\|)+/g, table => {
    if (table.includes('\n')) return table;
    const separator = table.match(/\|(?::?-+:?\|)+/);
    if (!separator) return table;
    const cols = (separator[0].match(/:?-+:?/g) ?? []).length;
    if (cols === 0) return table;

    const pipesPerRow = cols + 1;
    const rows: string[] = [];
    let rowStart = 0;
    let rowPipes = 0;
    for (let i = 0; i < table.length; i++) {
      if (table[i] !== '|') continue;
      rowPipes++;
      if (rowPipes === pipesPerRow) {
        rows.push(table.slice(rowStart, i + 1).trim());
        rowStart = i + 1;
        rowPipes = 0;
      }
    }
    if (rows.length < 2) return table;
    return `\n\n${rows.join('\n')}\n\n`;
  });

const hasDocumentContent = (content: unknown): boolean =>
  (typeof content === 'string' && content.trim().length > 0) ||
  (content !== null && content !== undefined && typeof content === 'object');

const DocumentArtifactContent = ({
  documentName,
}: {
  documentName: string;
}) => {
  const { collaborativeDocument, isLoading, startPolling, stopPolling } =
    useCollaborativeDocument({
      name: documentName,
    });

  const markdown = collaborativeDocument?.markdown;
  const content = collaborativeDocument?.content;
  const hasContent = hasDocumentContent(content);
  const hasPendingOperation = !!collaborativeDocument?.pendingOperation;
  const isUpdating = hasPendingOperation || (hasContent && !markdown);

  useEffect(() => {
    if (isUpdating) {
      startPolling(DOCUMENT_UPDATING_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [isUpdating, startPolling, stopPolling]);

  if (isLoading && !collaborativeDocument) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  if (markdown) {
    return (
      <MarkdownContent>{reflowSingleLineTables(markdown)}</MarkdownContent>
    );
  }

  if (typeof content === 'string' && content.trim()) {
    return <MarkdownContent>{reflowSingleLineTables(content)}</MarkdownContent>;
  }

  if (isUpdating) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  return (
    <div className={styles.emptyState}>
      <Text variant="body2" color="textSecondary">
        This document is empty.
      </Text>
    </div>
  );
};

const MetricCard = ReportMetricCard.extend({
  addNodeView() {
    return null;
  },
});

const MetricTable = ReportMetricTable.extend({
  addNodeView() {
    return null;
  },
});

const VariantCard = ReportVariantCard.extend({
  addNodeView() {
    return null;
  },
});

const PREVIEW_EDITOR_EXTENSIONS = [
  CommentMark,
  CommentDecorationsExtension,
  MetricCard,
  MetricTable,
  VariantCard,
];

const DocumentPreviewEditorInner = () => {
  const { collaborativeDocument, isPublished } =
    useCollaborativeDocumentContext();
  const updater = getTypeOrNull(collaborativeDocument?.updater);
  const hasHumanUpdater = updater
    ? identityKind(updater.name) === 'user'
    : false;

  return (
    <Flex
      display="flex"
      flexDirection="column"
      style={{
        gap: 'var(--encore-spacing-base)',
      }}
    >
      {collaborativeDocument?.updateTime && (
        <Flex
          display="flex"
          alignItems="center"
          style={{
            gap: 'var(--encore-spacing-tighter-2)',
          }}
        >
          <Text variant="body2" color="textSecondary">
            Last edited{' '}
            {DateUtils.xAgo(new Date(collaborativeDocument.updateTime))}
          </Text>
          {hasHumanUpdater && updater && (
            <IdentityChip size="small" identityRef={updater} />
          )}
        </Flex>
      )}
      <TipTapEditor
        readonly={isPublished}
        disableToolbar
        extensions={PREVIEW_EDITOR_EXTENSIONS}
      />
    </Flex>
  );
};

const DocumentPreviewEditor = ({
  documentName,
  renderActions,
}: {
  documentName: string;
  renderActions?: (documentName: string) => ReactNode;
}) => {
  const client = useApolloClient();
  const runningChats = useRunningChatNames();
  const hasRunningAgent = runningChats.size > 0;

  useEffect(() => {
    if (!hasRunningAgent) return undefined;
    const interval = setInterval(() => {
      client.refetchQueries({
        include: [CollaborativeDocumentDocument],
      });
    }, DOCUMENT_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasRunningAgent, client]);

  return (
    <ErrorBoundary
      renderFallback={() => (
        <DocumentArtifactContent documentName={documentName} />
      )}
    >
      <Suspense
        fallback={
          <div className={styles.loading}>
            <Spinner size={24} />
          </div>
        }
      >
        <CollaborativeDocumentProvider documentName={documentName}>
          {renderActions?.(documentName)}
          <DocumentPreviewEditorInner />
        </CollaborativeDocumentProvider>
      </Suspense>
    </ErrorBoundary>
  );
};

const isDocumentArtifact = (artifact: AttachmentFragment) =>
  !isExternalLink(artifact.uri) &&
  getResourceName(artifact.uri).startsWith('collaborativeDocuments/');

const useDocumentHref = (documentName: string) => {
  const { buildDocumentHref } = useDocumentNavigation();
  const [, documentId] = documentName.split('/');
  return documentId ? buildDocumentHref(documentId) : undefined;
};

const DocumentArtifactListItem = ({
  artifact,
  icon,
  onSelect,
  onDelete,
  onRename,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onSelect: () => void;
  onDelete?: () => void;
  onRename?: () => void;
}) => {
  const [menuOpened, setMenuOpened] = useState(false);
  const documentName = getResourceName(artifact.uri);
  const documentHref = useDocumentHref(documentName);
  const displayName = getArtifactDisplayName(artifact, UNTITLED_DOCUMENT_TITLE);
  const fullDocumentUrl = documentHref
    ? `${window.location.origin}${documentHref}`
    : undefined;
  const { collaborativeDocument } = useCollaborativeDocument({
    name: documentName,
    skip: !menuOpened,
  });

  const dateLabel = new Date(artifact.createTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const markdownContent =
    collaborativeDocument?.markdown ??
    (typeof collaborativeDocument?.content === 'string'
      ? collaborativeDocument.content
      : undefined);

  const handleDownloadMarkdown = () => {
    if (!markdownContent) return;
    const filename = `${sanitizeFilename(getArtifactDisplayName(artifact, 'document'))}.md`;
    downloadTextFile(markdownContent, filename);
  };

  return (
    <ArtifactListItem.Root
      onClick={createModifierClickHandler(documentHref, onSelect)}
    >
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={displayName}
        secondary={<>Document &middot; {dateLabel}</>}
      />
      <ArtifactListItem.Action>
        <ArtifactMenu.Root
          aria-label="Document actions"
          copyUrl={fullDocumentUrl}
          displayName={displayName}
          onOpen={() => setMenuOpened(true)}
          onRename={onRename}
          onDelete={onDelete}
        >
          <ArtifactMenu.Item
            icon={<RiFileLine size={16} />}
            onClick={() => {
              if (documentHref) {
                window.open(documentHref, '_blank');
              } else {
                onSelect();
              }
            }}
          >
            Open full document
          </ArtifactMenu.Item>
          <Copy
            value={() => markdownContent ?? ''}
            component={ArtifactMenu.Item}
            label="Copy as Markdown"
            icon="copy"
            disabled={!markdownContent}
          />
          <ArtifactMenu.Item
            icon={<RiDownloadLine size={16} />}
            onClick={handleDownloadMarkdown}
            disabled={!markdownContent}
          >
            Download as Markdown
          </ArtifactMenu.Item>
        </ArtifactMenu.Root>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};

const PopOutDocumentButton = ({ documentName }: { documentName: string }) => {
  const documentHref = useDocumentHref(documentName);

  if (!documentHref) {
    return null;
  }

  return (
    <Tooltip title="Open full document">
      <ButtonIcon
        size="small"
        onClick={() => window.open(documentHref, '_blank')}
        aria-label="Pop out document"
      >
        <RiExternalLinkLine size={16} />
      </ButtonIcon>
    </Tooltip>
  );
};

const DocumentArtifactHeaderActions = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => <PopOutDocumentButton documentName={getResourceName(artifact.uri)} />;

const DocumentDialogActions = ({ documentName }: { documentName: string }) => {
  return (
    <div className={styles.dialogActions}>
      <PopOutDocumentButton documentName={documentName} />
      <CopyAsMarkdownButton />
    </div>
  );
};

const DocumentArtifactCard = ({
  artifact,
  onDelete,
}: {
  artifact: AttachmentFragment;
  onDelete?: () => void;
}) => {
  const { openDialog, closeDialog } = useDialog();
  const documentName = getResourceName(artifact.uri);

  const handleNavigate = () => {
    openDialog({
      content: (
        <DialogComposite.Root static onClose={closeDialog}>
          <DialogComposite.Content>
            <DialogComposite.CloseButton />
            <DialogComposite.Header>
              <DialogComposite.Title>
                {getArtifactDisplayName(artifact, UNTITLED_DOCUMENT_TITLE)}
              </DialogComposite.Title>
            </DialogComposite.Header>
            <DialogComposite.Body className={styles.dialogBody}>
              <DocumentPreviewEditor
                documentName={documentName}
                renderActions={name => (
                  <DocumentDialogActions documentName={name} />
                )}
              />
            </DialogComposite.Body>
          </DialogComposite.Content>
        </DialogComposite.Root>
      ),
      dialogProps: { maxWidth: 'md', fullWidth: true },
    });
  };

  return (
    <ArtifactCardBase
      artifact={artifact}
      onNavigate={handleNavigate}
      onDelete={onDelete}
    />
  );
};

export const DocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'document',
    predicate: isDocumentArtifact,
    icon: () => ({ Icon: RiFileLine, color: '#6366F1' }),
    label: 'Document',
    category: 'Documents',
    render: artifact => (
      <DocumentPreviewEditor documentName={getResourceName(artifact.uri)} />
    ),
    renderHeaderActions: artifact => (
      <DocumentArtifactHeaderActions artifact={artifact} />
    ),
    renderListItem: (artifact, { onSelect, onDelete, onRename, icon }) => (
      <DocumentArtifactListItem
        artifact={artifact}
        icon={icon}
        onSelect={onSelect}
        onDelete={onDelete}
        onRename={onRename}
      />
    ),
    renderCard: (artifact, { onDelete }) => (
      <DocumentArtifactCard artifact={artifact} onDelete={onDelete} />
    ),
  }),
);
